import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { getLiveShare } from "../lib/api";
import { useLiveShareStream } from "../lib/useLiveShareStream";
import { getStyle, applySatelliteZoomCap } from "../lib/mapStyles";
import { useCountryConfig } from "../lib/useCountryConfig";
import { Radio, Clock, MapPin, AlertTriangle, Loader2, WifiOff } from "lucide-react";

const POLL_MS = 8000;
// Iter89 — staleness thresholds for the viewer status badge. Sender
// transmits at least every ~5s (see LiveSharePanel's MIN_INTERVAL_MS),
// so anything past that with no update means degraded connectivity;
// past STALE_MS means we can no longer trust the pin as "current".
const WEAK_MS = 15000;
const STALE_MS = 45000;

const fmtCountdown = (expiresAt) => {
  if (!expiresAt) return "—";
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (ms <= 0) return "Expiré";
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const fmtAgo = (iso) => {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return `il y a ${s} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `il y a ${m} min`;
  const h = Math.floor(m / 60);
  return `il y a ${h} h`;
};

// Iter89 — 4 distinct viewer states (spec: never show an old coordinate
// as if it were current). Order matters: explicit "ended" always wins.
const deriveStatus = (snapshot) => {
  if (!snapshot) return "loading";
  if (!snapshot.active) return "ended";
  const ms = snapshot.last_updated ? Date.now() - new Date(snapshot.last_updated).getTime() : Infinity;
  if (ms < WEAK_MS) return "live";
  if (ms < STALE_MS) return "weak";
  return "stale";
};

const STATUS_COLORS = {
  live: "#10B981",   // emerald — matches the original always-green dot
  weak: "#F59E0B",   // amber
  stale: "#9CA3AF",  // neutral gray — explicitly NOT green (not current)
  ended: "#9CA3AF",
};

const StatusBadge = ({ status }) => {
  if (status === "live") {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-400/50 text-emerald-200 text-[10px] font-bold uppercase tracking-[0.18em]" data-testid="live-view-status">
        <Radio className="w-3 h-3 animate-pulse" /> En direct
      </span>
    );
  }
  if (status === "weak") {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-500/15 border border-amber-400/50 text-amber-200 text-[10px] font-bold uppercase tracking-[0.18em]" data-testid="live-view-status">
        <WifiOff className="w-3 h-3" /> Connexion faible
      </span>
    );
  }
  if (status === "stale") {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-neutral-500/20 border border-neutral-400/40 text-neutral-200 text-[10px] font-bold uppercase tracking-[0.18em]" data-testid="live-view-status">
        <Clock className="w-3 h-3" /> Position périmée
      </span>
    );
  }
  if (status === "ended") {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-neutral-500/20 border border-neutral-400/40 text-neutral-200 text-[10px] font-bold uppercase tracking-[0.18em]" data-testid="live-view-status">
        Partage terminé
      </span>
    );
  }
  return null;
};

export default function LiveViewPage() {
  const { token } = useParams();
  const cfg = useCountryConfig();
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [, force] = useState(0);

  const mapRef = useRef(null);
  const containerRef = useRef(null);

  // Tick every second so the countdown AND the live/weak/stale status
  // (derived from `snapshot.last_updated`, no state of its own) stay current.
  useEffect(() => {
    const id = setInterval(() => force((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // Poll snapshot every POLL_MS — resilience fallback in case the
  // WebSocket below can't connect (corporate proxy, etc.). The socket
  // is the fast path; this just guarantees we're never MORE than
  // POLL_MS stale even if it's down.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const data = await getLiveShare(token);
        if (cancelled) return;
        setSnapshot(data);
        setLoading(false);
      } catch (e) {
        if (cancelled) return;
        const code = e?.response?.status;
        setError(code === 404 ? "not_found" : "load_failed");
        setLoading(false);
      }
    };
    tick();
    const id = setInterval(tick, POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [token]);

  // Iter89 — Real-time path: WebSocket push from the sender's own
  // PATCH .../position call (see routers/live_share.py), fanned out
  // through the same room-bus infra as alert intervention updates.
  // Auto-reconnects (see useLiveShareStream) and resubscribes to this
  // SAME token — never spins up a second session.
  useLiveShareStream(token, {
    onPosition: (msg) => {
      setSnapshot((prev) => {
        const base = prev || {};
        // A "snapshot" message may carry active=false for an already
        // expired/stopped share — respect it. A "position.update"
        // message has no `active` field, so keep whatever we had.
        const active = "active" in msg ? msg.active : base.active;
        return {
          ...base,
          last_lat: msg.lat,
          last_lng: msg.lng,
          last_accuracy: msg.accuracy,
          last_heading: msg.heading,
          last_speed: msg.speed,
          last_updated: msg.last_updated,
          active,
        };
      });
      setLoading(false);
    },
    onEnded: () => {
      setSnapshot((prev) => (prev ? { ...prev, active: false } : prev));
    },
  });

  // Init map + sync marker
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return undefined;
    let map;
    try {
      map = new maplibregl.Map({
        container: containerRef.current,
        style: getStyle("hybrid"),
        center: [-8.0029, 12.6392],
        zoom: 6,
        attributionControl: false,
        pitchWithRotate: false,
        dragRotate: false,
      });
    } catch (e) {
      console.error("[LiveView] map init failed", e);
      return undefined;
    }
    applySatelliteZoomCap(map, "hybrid");
    map.addControl(new maplibregl.AttributionControl({ compact: true }), "bottom-right");
    map.touchZoomRotate.disableRotation();
    mapRef.current = map;
    const ro = new ResizeObserver(() => { try { map.resize(); } catch (_e) { /* noop */ } });
    ro.observe(containerRef.current);
    // Final safety resize once the container is settled.
    const r = setTimeout(() => { try { map.resize(); } catch (_e) { /* noop */ } }, 250);
    return () => { clearTimeout(r); ro.disconnect(); map.remove(); mapRef.current = null; };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !snapshot?.last_lat || !snapshot?.last_lng) return undefined;
    // Iter89 — never paint a stale fix in the same "live" green as a
    // fresh one (spec: never show an old coordinate as if current).
    const dotColor = STATUS_COLORS[deriveStatus(snapshot)] || STATUS_COLORS.live;
    const data = {
      type: "FeatureCollection",
      features: [{
        type: "Feature",
        properties: { accuracy: snapshot.last_accuracy || 0 },
        geometry: { type: "Point", coordinates: [snapshot.last_lng, snapshot.last_lat] },
      }],
    };
    const apply = () => {
      let src = map.getSource("live-pos");
      if (!src) {
        map.addSource("live-pos", { type: "geojson", data });
        map.addLayer({
          id: "live-acc",
          type: "circle",
          source: "live-pos",
          paint: {
            "circle-radius": ["interpolate", ["linear"], ["coalesce", ["get", "accuracy"], 0], 0, 12, 20, 22, 100, 40, 500, 70],
            "circle-color": dotColor,
            "circle-opacity": 0.18,
            "circle-stroke-color": dotColor,
            "circle-stroke-width": 1.5,
            "circle-stroke-opacity": 0.6,
          },
        });
        map.addLayer({
          id: "live-dot",
          type: "circle",
          source: "live-pos",
          paint: {
            "circle-radius": 9,
            "circle-color": dotColor,
            "circle-stroke-color": "#ffffff",
            "circle-stroke-width": 3,
          },
        });
      } else {
        src.setData(data);
        map.setPaintProperty("live-acc", "circle-color", dotColor);
        map.setPaintProperty("live-acc", "circle-stroke-color", dotColor);
        map.setPaintProperty("live-dot", "circle-color", dotColor);
        map.flyTo({ center: [snapshot.last_lng, snapshot.last_lat], zoom: 15.5, speed: 0.9, essential: true });
      }
    };
    if (map.isStyleLoaded()) apply();
    else map.once("load", apply);
    return undefined;
  }, [snapshot?.last_lat, snapshot?.last_lng, snapshot?.last_accuracy, snapshot?.active, snapshot?.last_updated]);

  if (error === "not_found") {
    return (
      <div className="min-h-[100dvh] flex flex-col items-center justify-center px-6 text-center bg-background text-foreground" data-testid="live-view-not-found">
        <AlertTriangle className="w-12 h-12 text-amber-500 mb-3" />
        <h1 className="font-display text-xl font-black tracking-tight">Lien invalide</h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-sm">
          Ce partage en direct n&apos;existe pas ou a été supprimé.
        </p>
      </div>
    );
  }

  const status = deriveStatus(snapshot);
  const hasPos = snapshot?.last_lat != null && snapshot?.last_lng != null;

  return (
    <div className="h-[100dvh] flex flex-col bg-background text-foreground overflow-hidden" data-testid="live-view-page">
      {/* Mali flag stripe */}
      <div className="flex h-2 shrink-0">
        <div className="flex-1 bg-mali-green" />
        <div className="flex-1 bg-mali-yellow" />
        <div className="flex-1 bg-mali-red" />
      </div>

      <header className="px-5 py-3 bg-mali-navy text-white flex items-center justify-between">
        <div className="min-w-0">
          <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-mali-yellow" data-testid="live-view-brand">{(cfg.brand_name || "").toUpperCase()} · Partage en direct</div>
          <h1 className="font-display text-lg font-black tracking-tight mt-0.5 truncate">
            {snapshot?.label || (snapshot ? `Partage de ${snapshot.owner_phone_masked || "—"}` : "Chargement…")}
          </h1>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {snapshot ? <StatusBadge status={status} /> : null}
        </div>
      </header>

      <div className="relative flex-1 min-h-[60vh]">
        <div ref={containerRef} style={{ position: "absolute", inset: 0 }} data-testid="live-view-map" />
        {(loading || !hasPos) && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 bg-background/60 backdrop-blur-sm z-10 pointer-events-none">
            <Loader2 className="w-6 h-6 animate-spin text-mali-red mb-2" />
            <p className="text-sm font-bold">
              {loading ? "Chargement du partage…" : "En attente de la première position…"}
            </p>
          </div>
        )}
      </div>

      {/* Footer info bar */}
      <div className="px-4 py-3 bg-background border-t border-border grid grid-cols-3 gap-3" data-testid="live-view-info">
        <div>
          <div className="text-[9px] font-bold uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-1">
            <Clock className="w-3 h-3" /> Expire
          </div>
          <div className="font-bold text-sm mt-0.5">{snapshot ? fmtCountdown(snapshot.expires_at) : "—"}</div>
        </div>
        <div>
          <div className="text-[9px] font-bold uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-1">
            <MapPin className="w-3 h-3" /> Précision
          </div>
          <div className="font-bold text-sm mt-0.5">
            {snapshot?.last_accuracy != null ? `±${Math.round(snapshot.last_accuracy)} m` : "—"}
          </div>
        </div>
        <div>
          <div className="text-[9px] font-bold uppercase tracking-[0.2em] text-muted-foreground">Mise à jour</div>
          <div className="font-bold text-sm mt-0.5">{snapshot ? fmtAgo(snapshot.last_updated) : "—"}</div>
        </div>
      </div>
    </div>
  );
}
